import { InfoCircleOutlined } from '@ant-design/icons';
import { payrollApi } from 'Api/payroll';
import { viewPayrollApi } from 'Api/payrollView';
import { Button, Col, Modal, Row, Table, Tag, Tooltip } from 'antd';
import Buttons from 'components/Global/Buttons';
import InputNumberField from 'components/Global/InputNumberField';
import SelectDropdown from 'components/Global/SelectDropdown';
import TextareaFieldInput from 'components/Global/textareaField';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	capitalizeWords,
	formatNumber,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import GlobalHeader from '../../GlobalHeader';
import styles from './index.module.scss';
import './index.scss';

type Props = {
	selectedPayrollId: string;
	onlyView: boolean;
	setIsUniversalLoading: any;
	headerData: any;
	payPeriodId: any;
	year: number;
	showSteps: boolean;
};

const TotalEarningTable: React.FC<Props> = (props: Props) => {
	const {
		selectedPayrollId,
		onlyView,
		setIsUniversalLoading,
		headerData,
		payPeriodId,
		year,
		showSteps,
	} = props;

	const [earningsData, setEarningsData] = useState<any[]>([]);
	const [isEarningsLoading, setEarningsLoading] = useState(false);
	const [columns, setColumns] = useState<any>([]);
	const [rowData, setRowData] = useState<any[]>([]);
	const { data } = useSelector((state: any) => state?.userProfile);
	const [page, setPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageSize, setPageSize] = useState(31);
	const [earningsOptions, setEarningsOptions] = useState<any>({});
	const [payrollSpecificData, setPayrollSpecificData] = useState<any>({});

	const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);

	const [earningDetails, setEarningDetails] = useState<any>({
		employeeId: '',
		amount: null,
		notes: '',
		earningType: '',
	});

	const [formError, setFormError] = useState<any>({
		employeeId: false,
		amount: false,
		notes: false,
		earningType: false,
	});

	const [isLoading, setIsLoading] = useState(false);

	const [isAddEarningModal, setIsAddEarningModal] = useState(false);

	const tableChangeHandler = (pagination: any, filters: any, sorter: any) => {
		setTotalRecords(pagination.total);
		setPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const { conversionRate, usdCurrencyToggle, isUsdCurrency } = useSelector(
		(state: any) => state.usdCurrencyToggle
	);
	const closeEarningModal = () => {
		setIsAddEarningModal(false);
		setFormError({
			employeeId: false,
			amount: false,
			notes: false,
			earningType: false,
		});
		setEarningDetails({
			employeeId: '',
			amount: null,
			notes: '',
			earningType: '',
		});
	};

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: isLoading,
			isSubmit: false,
			onclick: () => {
				closeEarningModal();
			},
		},
		{
			text: 'Save',
			isLoading: isLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const handleSubmit = async () => {
		let checkFormError = validateFormData(
			{ ...earningDetails },
			{ ...formError }
		);

		checkFormError = {
			...checkFormError,
		};
		setFormError(checkFormError);
		if (hasFormError(checkFormError)) {
			return;
		}
		try {
			setIsLoading(true);
			const data = {
				employeeId: earningDetails.employeeId,
				payrollId: selectedPayrollId,
				earningId: earningDetails.earningType,
				amount: earningDetails.amount,
				reason: earningDetails.notes,
			};
			const apiResponse = await payrollApi.createEmployeeSpecificEarnings(
				data
			);
			const isUpdated = apiResponse.data.data.isUpdated ?? false;
			const employeeName = employeeOptions.find(
				(emp) => emp.value === data.employeeId
			).fullName;
			toastText(
				`Earning successfully ${
					isUpdated ? 'updated' : 'added'
				} for ${employeeName}`,
				'success'
			);
			closeEarningModal();
			fetchEarnings(selectedPayrollId);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in adding new earning';

			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const formatData = (rows: any) => {
		const formattedRowData = rows?.map((item: any) => {
			const formattedItem: any = {
				__raw__: item,
			};

			for (const key in item) {
				if (key == 'Employee') {
					formattedItem[key] = item[key];
				} else if (!usdCurrencyToggle && isUsdCurrency) {
					formattedItem[key] = formatNumber(
						Number(item[key]),
						data?.CompanyCurrencies,
						true
					);
				} else {
					formattedItem[key] = formatNumber(
						Number(item[key] * (conversionRate ?? 1)),
						data?.CompanyCurrencies
					);
				}
			}

			return formattedItem;
		});
		setEarningsData(formattedRowData);
	};

	const generateColumns = (
		columnsData: any,
		payrollSpecificDataParam?: any
	) => {
		// Use parameter first, fallback to state
		const payrollSpecificToUse =
			payrollSpecificDataParam || payrollSpecificData || {};
		const flattenedColumns: any[] = [];

		// Add Employee column first
		flattenedColumns.push({
			title: 'Employees',
			dataIndex: 'Employee',
			key: 'Employee',
			className: 'bg-white',
			fixed: 'left',
			width: 250,
			render: (text: string, record: any) => {
				return (
					<span
						style={{
							cursor: 'default',
							display: 'flex',
							alignItems: 'center',
							gap: '4px',
						}}
					>
						{text}
					</span>
				);
			},
		});

		// Add all earning columns with payroll-specific indicators
		columnsData.forEach((column: any) => {
			const { title } = column;
			flattenedColumns.push({
				id: Math.random(),
				title: capitalizeWords(title),
				dataIndex: title,
				key: title,
				width: '200px',
				className: 'bg-white',
				render: (value: any, record: any) => {
					const raw = record.__raw__;

					const isTotalRow = raw?.Employee === 'Total';

					if (isTotalRow) {
						return <span>{value}</span>;
					}

					const employeeId = raw?.employeeId;
					const payrollSpecific =
						payrollSpecificToUse?.[employeeId]?.[title];

					const isPayrollSpecific =
						payrollSpecific?.isPayrollSpecific;
					if (isPayrollSpecific) {
						const { reason } = payrollSpecific;

						let tooltipContent = (
							<div style={{ maxWidth: '250px' }}>
								<div style={{ marginBottom: '4px' }}>
									<span>Reason : </span>{' '}
									{!invalidText(reason)
										? reason
										: 'added for this payroll'}
								</div>
							</div>
						);

						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
								}}
							>
								<span>{value}</span>
								<Tooltip title={tooltipContent} placement="top">
									<InfoCircleOutlined
										style={{
											color: '#584495',
											fontSize: '14px',
										}}
									/>
								</Tooltip>
							</div>
						);
					}

					return <span>{value}</span>;
				},
			});
		});

		// Add Total column at the end
		flattenedColumns.push({
			title: 'Total',
			dataIndex: 'total',
			key: 'total',
			width: '150px',
			className: 'bg-white',
		});

		return flattenedColumns;
	};

	const fetchEarnings = async (payRollId: string) => {
		try {
			let response;
			setEarningsLoading(true);

			setIsUniversalLoading(true);
			if (onlyView) {
				response = await viewPayrollApi.getAllEmployeeEarnings({
					payrollId: payRollId,
					page: page,
					pageSize: pageSize - 1,
				});
			} else {
				response = await payrollApi.getPayrollEarnings({
					payrollId: payRollId,
					page: page,
					pageSize: pageSize - 1,
				});
			}
			const { columns, rows } = response?.data?.data;

			const payrollSpecific =
				response?.data?.data?.payrollSpecificData || {};
			setPayrollSpecificData(payrollSpecific);

			const generatedColumns = generateColumns(columns, payrollSpecific);

			setEmployeeOptions(response?.data?.data?.payrollEmployeeOptions);
			setEarningsOptions(response?.data?.data?.masterEarningsOptions);
			setTotalRecords(response?.data?.data?.totalRecords);
			setRowData(rows);
			formatData(rows);
			setColumns(generatedColumns);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching earnings';

			toastText(errorMessage, 'error');
		} finally {
			setEarningsLoading(false);
			setIsUniversalLoading(false);
		}
	};

	useEffect(() => {
		if (selectedPayrollId) {
			fetchEarnings(selectedPayrollId);
		}
	}, [selectedPayrollId, page, pageSize]);

	useEffect(() => {
		formatData(rowData);
	}, [usdCurrencyToggle]);

	const handleChange = (
		value: string | number | null,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		if (typeof value === 'number') {
			if (value <= 0) {
				return;
			}
		}

		OnChange(value, name);
	};

	const OnChange = (value: string | number | null, key: string) => {
		setEarningDetails((prev: any) => {
			if (key === 'employeeId') {
				return {
					employeeId: value, // set new employee
					earningType: null, // reset earning type
					amount: null, // reset amount
				};
			}

			return {
				...prev,
				[key]: value,
			};
		});

		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};
	return (
		<div className="payroll-steps-table">
			<GlobalHeader
				headerData={headerData}
				toggleRequired={true}
				fetchAllData={() => fetchEarnings(selectedPayrollId)}
				isLoading={isEarningsLoading}
			/>
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					marginBottom: '4px',
					fontSize: '14px',
				}}
			>
				{/* Left side - Statutory Components toggle */}

				{/* Right side - Action buttons */}
				{!onlyView && (
					<div style={{ display: 'flex', gap: '8px' }}>
						<Button
							type="primary"
							onClick={() => setIsAddEarningModal(true)}
							disabled={isEarningsLoading}
						>
							Add Earning
						</Button>
					</div>
				)}
			</div>
			<Table
				className="total-earning-table"
				columns={columns}
				dataSource={earningsData}
				rowClassName={(record, index) =>
					index === earningsData.length - 1
						? 'sticky-row  color-purple font-bold'
						: ''
				}
				bordered
				scroll={{
					y: showSteps
						? 'calc(100vh - 455px)'
						: 'calc(100vh - 375px)',
				}}
				pagination={{
					total: totalRecords,
					current: page,
					pageSize: pageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				rowKey={(record: any) => record.id}
				loading={isEarningsLoading}
				onChange={tableChangeHandler}
			/>

			{isAddEarningModal && (
				<Modal
					open={isAddEarningModal}
					onOk={() => {}}
					onCancel={closeEarningModal}
					okText={'Save'}
					centered
					closable={false}
					width={650}
					footer={null}
					title={<h2>Add Earning</h2>}
					rootClassName="addEarningModalRoot"
				>
					<p className={styles['form-container-head-warning']}>
						<b>
							{' '}
							<sup>*</sup>
						</b>{' '}
						Indicated mandatory fields
					</p>
					<div
						style={{
							marginTop: '12px',
							padding: '12px 16px',
							backgroundColor: '#f6f4ff', // Light purple background
							borderRadius: '6px',
							fontSize: '14px',
							color: '#584495', // Main purple text color
							display: 'flex',
							alignItems: 'flex-start',
							gap: '4px',
						}}
					>
						<div>
							<strong style={{ color: '#584495' }}>Note: </strong>
							Any Earning added will be applied to this payroll
							only.
						</div>
					</div>
					<Row className={styles['form-container-card']} gutter={50}>
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<SelectDropdown
								placeholder="Employee Name"
								options={employeeOptions}
								value={earningDetails?.employeeId}
								onChange={(value: any) =>
									handleChange(value, 'employeeId', true)
								}
								size="large"
								required={true}
								helperText="Employee Name required"
								label="Employee Code | Employee Name"
								isError={formError.employeeId}
							/>
						</Col>{' '}
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<SelectDropdown
								placeholder="Earning Type"
								options={
									earningDetails?.employeeId
										? earningsOptions[
												earningDetails.employeeId
										  ] || []
										: []
								}
								value={earningDetails?.earningType}
								disabled={invalidText(
									earningDetails?.employeeId
								)}
								onChange={(value: any) =>
									handleChange(value, 'earningType', true)
								}
								size="large"
								required={true}
								helperText="Earning Type required"
								label="Earning Type"
								isError={formError.earningType}
							/>
						</Col>
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<InputNumberField
								name="amount"
								value={earningDetails.amount}
								label="Amount"
								required={true}
								helperText="Amount must be greater than zero"
								onChange={(value) =>
									handleChange(value, 'amount', true)
								}
								// suffix={'ZMW'}
								isError={formError.amount}
							/>
						</Col>
						<Col
							span={24}
							className={`${styles['col']} margin-top-10`}
						>
							<TextareaFieldInput
								name="Reason"
								value={earningDetails.notes}
								label="Reason"
								required={true}
								helperText="Reason is required"
								onChange={(value) =>
									handleChange(value, 'notes', true)
								}
								isError={formError.notes}
							/>
						</Col>
					</Row>

					<div className={styles['buttons']}>
						<Buttons buttons={myButtons} />
					</div>
				</Modal>
			)}
		</div>
	);
};

export default TotalEarningTable;
